<template lang="pug">
#family-edit(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span(v-if="senderType == 0") 家族ユーザー情報修正
        span(v-if="senderType == 1") 家族メールユーザー情報修正

    .box-body.register-body

      FormLabel(key-name="部屋名・利用者名" :value="residentName")
      FormLabel(key-name="家族氏名" :value="name" v-if="senderType == 0")
      FormInput(
        v-if="senderType == 1"
        key-name="家族氏名"
        required
        name="family-name"
        v-model="name"
        type="text"
        placeholder="家族氏名"
        :maxlength="32"
        :validation-rules="{ required: true, max: 32 }"
        :styles="{ width: '340px' }")
      FormLabel(key-name="性別" :value="gender" v-if="senderType == 0")
      FormInputGender(v-if="senderType == 1" key-name="性別" v-model="gender")
      FormLabel(key-name="生年月日" :value="_birthDay" v-if="senderType == 0")
      FormInputSplitDate(
        v-if="senderType == 1"
        key-name="生年月日"
        :year.sync="year"
        :month.sync="month"
        :day.sync="day"
        validationDateKeyName="生年月日")
      FormLabel(key-name="電話番号" :value="tel" v-if="senderType == 0")
      FormInput(
        v-if="senderType == 1"
        key-name="電話番号"
        required
        name="tel"
        v-model="tel"
        type="text"
        placeholder="電話番号"
        option-text="-（ハイフン）なしで入力してください。"
        :maxlength="11"
        :validation-rules="{ required: true, numeric: true, max: 13, min: 5 }"
        :styles="{ width: '340px' }")
      FormLabel(key-name="続柄" :value="relation" v-if="senderType == 0")
      FormInput(
        v-if="senderType == 1"
        key-name="続柄"
        required
        name="relation"
        v-model="relation"
        type="text" placeholder="続柄"
        :maxlength="10"
        :validation-rules="{ required: true, max: 10 }"
        :styles="{ width: '340px' }")
      FormInput(
        key-name="役割"
        name="position"
        v-model="position"
        type="text" placeholder="役割"
        option-text="設定内容はご家族には公開されません。"
        :maxlength="32"
        :validation-rules="{ max: 32 }"
        :styles="{ width: '340px' }")
      FormInput(
        v-if="senderType == 1"
        key-name="メールアドレス"
        required
        name="email"
        v-model="email"
        type="text"
        placeholder="メールアドレス"
        :maxlength="100"
        :validation-rules="{ required: true, max: 100, email: true, equal_value: confirmEmail }"
        :styles="{ width: '540px' }")
      FormInput(
        v-if="senderType == 1"
        key-name="メールアドレス（確認）"
        required
        name="confirm-email"
        v-model="confirmEmail"
        type="text"
        placeholder="メールアドレス（確認）"
        :maxlength="100"
        :validation-rules="{ required: true, max: 100, email: true }"
        :styles="{ width: '540px' }")
      .register-row(v-if="senderType == 0")
        .register-group
          .register-key
            span.key-name 個別使用設定（家族からの連絡）
          div
            UsableFunctionSwitch(v-model="permittedFunctions.opinion")
      .register-row(v-if="senderType == 0")
        .register-group
          .register-key
            span.key-name 個別使用設定（写真印刷依頼）
          div
            UsableFunctionSwitch(v-model="permittedFunctions.picture")

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.register-btn(@click="showConfirmModal" type="button") 更新
        button.btn.cancel-btn(@click="gotoBeforePage" type="button") キャンセル

  OkCancelModal(v-model="showUpdateModal" title="家族ユーザー情報を更新しますか？" @ok="editFamily")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoBeforePage")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { needRefreshesStore } from "@/store/store";
import { FamilyListState, SendMessagesState } from "@/store/NeedRefreshes";

import CustomValidate from "@/commons/utils/CustomValidate";
import { PcUrl } from "@/commons/constants/PcUrl";
import FamiliesService, { FamilyGetModel, FamilyPatchModel, FamilyPermittedFunctions } from "@/commons/services/Families";
import { SenderType } from "@/commons/models/Resident";
import MailFamiliesService, { MailFamiliesPatchModel } from "@/commons/services/MailFamilies";
import SystemError from "@/commons/models/SystemError";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormLabel from "@/pc/components/forms/FormLabel.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormInputSelect from "@/pc/components/forms/FormInputSelect.vue";
import FormInputGender from "@/pc/components/forms/FormInputGender.vue";
import FormInputSplitDate from "@/pc/components/forms/FormInputSplitDate.vue";
import UsableFunctionSwitch from "@/pc/basics/UsableFunctionSwitch.vue";

/**
 * 家族メールユーザー更新画面コンポーネント
 */
@Component({
  components: {
    FormLabel,
    FormInputSelect,
    FormInput,
    FormInputGender,
    FormInputSplitDate,
    UsableFunctionSwitch,
    PageLoading,
    ValidationAlert,
    OkCancelModal,
    OkModal
  },
  $_veeValidate: { validator: "new" }
})
export default class FamilyRegister extends Vue {
  /** 家族ユーザーサービス */
  @Inject("FamiliesService") familiesService!: FamiliesService;
  /** 家族メールユーザーサービス */
  @Inject("MailFamiliesService") mailFamiliesService!: MailFamiliesService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 送信種別 */
  senderType: SenderType = SenderType.LINE;
  /** 利用者名。実際には{部屋名}・{利用者名}の形式 */
  residentName: string = "";
  /** 家族名 */
  name: string = "";
  /** 生年月日・年 */
  year: string = "";
  /** 生年月日・月 */
  month: string = "";
  /** 生年月日・日 */
  day: string = "";
  /** 性別 */
  gender: string = "無回答";
  /** 電話番号 */
  tel: string = "";
  /** 続柄 */
  relation: string = "";
  /** 役割 */
  position: string = "";
  /** 機能の使用可否 */
  permittedFunctions: FamilyPermittedFunctions = {
    opinion: true,
    picture: true
  };
  /** 更新日時 */
  updated: number = 0;

  /** メールアドレス */
  email: string = "";
  /** メールアドレス(確認) */
  confirmEmail: string = "";

  /** APIから返却された場合に表示するエラーメッセージ */
  errorMessage: string = "";

  get _birthDay(): string {
    return `${this.year}年${this.month}月${this.day}日`;
  }

  /**
   * vue lifecycle created
   */
  public created(): void {
    CustomValidate.addCustomValidate(this.$validator);

    return;
  }

  /**
   * vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();

    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    // 初期値を格納する
    const family: FamilyGetModel = await this.getFamily();

    this.senderType = family.senderType;
    this.residentName = `${family.room}・${family.name}`;
    this.name = family.familyName ? family.familyName : "";
    this.year = "";
    this.month = "";
    this.day = "";
    if (family.familyBirthDay != undefined) {
      this.year = this.$dateUtil.convertNumberToString(family.familyBirthDay, "yyyy");
      this.month = this.$dateUtil.convertNumberToString(family.familyBirthDay, "mm", false);
      this.day = this.$dateUtil.convertNumberToString(family.familyBirthDay, "dd", false);
    }
    this.gender = family.familyGender ? family.familyGender : "";
    this.tel = family.familyTel ? family.familyTel : "";
    this.relation = family.familyRelation ? family.familyRelation : "";
    this.position = family.familyPosition ? family.familyPosition : "";
    this.email = family.familyMailaddress ? family.familyMailaddress : "";
    this.confirmEmail = family.familyMailaddress ? family.familyMailaddress : "";
    this.permittedFunctions = family.familyPermittedFunctions;
    this.updated = family.updated ? Number(family.updated) : 0;

    this.isBusy = false;

    return;
  }

  /**
   * ### 家族を取得する
   *
   * - 家族単一を取得するAPIはないので、利用者にひもづく家族を取得する
   * - 家族の中から家族IDが同一のものを修正対象の家族と判断する
   * - 見つからなかった場合は404画面に遷移させたいので例外を投げる
   */
  public async getFamily(): Promise<FamilyGetModel> {
    const residentId: number = Number(Number(this.$route.params.residentid));
    const familyId: number = Number(Number(this.$route.params.id));

    const families: Array<FamilyGetModel> = await this.familiesService.getsInResident(userStore, residentId);

    for (const family of families) {
      if (family.familyId == familyId) {
        return family;
      }
    }

    let systemError: SystemError = new SystemError(`family is notfound. residentId: ${residentId}, familyId: ${familyId}.`);
    systemError.statusCode = 404;
    throw systemError;
  }

  /**
   * ### 確認モーダルを表示する
   *
   * - バリデーターを使用してバリデーションする
   * - 生年月日のバリデーションは自前で行う
   * - バリデーションでエラーが無ければ確認モーダルを表示する
   */
  public async showConfirmModal(): Promise<void> {
    const birthdayValidationResult: boolean = await this.validateBirthday();
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult && birthdayValidationResult) {
      this.showValidationAlert = false;
      this.showUpdateModal = true;
    } else {
      this.showValidationAlert = true;
    }
    return;
  }

  /**
   * ### 生年月日の存在するかしないかのバリデーション
   *
   * - 生年月日が1つでも入力されていればバリデーション対象として存在確認する
   * - 生年月日が1つも入力されていない場合はバリデーションせず、OKとする
   */
  public async validateBirthday(): Promise<boolean> {
    this.$validator.errors.remove("date");

    if (this.year || this.month || this.day) {
      const birthday: number = this.joinBirthday();
      const { valid, errors } = await this.$validator.verify(birthday, "exist_date", { name: "生年月日", bails: false });
      if (!valid) {
        for (const error of errors) {
          this.$validator.errors.add({ field: "date", msg: error });
        }
      }
      return valid;
    } else {
      return true;
    }
  }

  /**
   * ### 分割している生年月日を結合する
   *
   * - DBに登録する場合はnumber型で登録する設計になっている
   * - そのため月と日は1桁の場合は0埋めする
   */
  public joinBirthday(): number {
    let month: string = String(this.month);
    if (month.length == 1) {
      month = `0${month}`;
    }

    let day: string = String(this.day);
    if (day.length == 1) {
      day = `0${day}`;
    }

    const birthday: number = Number(`${this.year}${month}${day}000000000`);

    return birthday;
  }

  /**
   * ### 家族ユーザー及び家族メールユーザーを更新する
   */
  public async editFamily(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    // 送信種別に応じて更新処理を分岐する
    // LINE
    let result: boolean = true;
    if (this.senderType == SenderType.LINE) {
      result = await this.updateFamily();
    }
    // メール
    else if (this.senderType == SenderType.MAIL) {
      result = await this.updateMailFamily();
    }
    // あり得ないが、その他の場合は例外とする
    else {
      throw new SystemError(`FamilyEdit.editFamily() senderType is invalid ${this.senderType}`);
    }

    if (result == true) {
      // 家族一覧を再読み込みさせるようstoreを更新
      let familyList: FamilyListState = needRefreshesStore.familyList;
      familyList.familyList = true;
      needRefreshesStore.setFamilyList(familyList);
      // メッセージ送信の宛先が変更になるので再読み込みさせるようstoreを更新
      let sendMessageState: SendMessagesState = needRefreshesStore.sendMessage;
      sendMessageState.sendMessages = true;
      needRefreshesStore.setSendMessage(sendMessageState);

      // 更新成功ダイアログを表示する
      this.showUpdateSuccessModal = true;
    }

    this.isBusy = false;

    return;
  }

  /**
   * ### 家族ユーザーを更新する
   *
   * @return 処理結果(成功：true、失敗：false)
   */
  public async updateFamily(): Promise<boolean> {
    try {
      const requestModel: any = this.createFamilyRequestModel();
      const residentId: number = Number(this.$route.params.residentid);

      const familyId: number = Number(this.$route.params.id);
      await this.familiesService.patch(userStore, residentId, familyId, requestModel);
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
        return false;
      } else {
        throw error;
      }
    }

    return true;
  }

  /**
   * ### 家族ユーザー更新用のパラメータを作成する
   *
   * - このメソッドが呼び出される際は既に値のバリデーションが済んでいるものとする
   * - 生年月日が入力されている場合は生年月日を結合してnumber型に変換する
   */
  public createFamilyRequestModel(): any {
    let birthday: any = undefined;
    if (this.year && this.month && this.day) {
      birthday = this.joinBirthday();
    }

    const requestModel: FamilyPatchModel = {
      familyName: this.$stringUtil.convertYen(this.name),
      familyTel: this.tel,
      familyRelation: this.$stringUtil.convertYen(this.relation),
      familyPosition: this.position ? this.$stringUtil.convertYen(this.position) : "",
      familyGender: this.gender,
      familyBirthDay: birthday,
      familyPermittedFunctions: this.permittedFunctions,
      updated: this.updated
    };

    return requestModel;
  }

  /**
   * ### 家族メールユーザーを更新する
   *
   * @return 処理結果(成功：true、失敗：false)
   */
  public async updateMailFamily(): Promise<boolean> {
    try {
      const requestModel: MailFamiliesPatchModel = this.createMailFamilyRequestModel();
      const residentId: number = Number(this.$route.params.residentid);
      const familyId: number = Number(this.$route.params.id);

      await this.mailFamiliesService.patch(userStore, residentId, familyId, requestModel);
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage =
          "既に同一のメールアドレスが登録されている。または他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
        return false;
      } else {
        throw error;
      }
    }

    return true;
  }

  /**
   * ### 家族メールユーザー更新用のパラメータを作成する
   *
   * - このメソッドが呼び出される際は既に値のバリデーションが済んでいるものとする
   * - 生年月日が入力されている場合は生年月日を結合してnumber型に変換する
   */
  public createMailFamilyRequestModel(): MailFamiliesPatchModel {
    let birthday: any = undefined;
    if (this.year && this.month && this.day) {
      birthday = this.joinBirthday();
    }

    const requestModel: MailFamiliesPatchModel = {
      familyMailaddress: this.email,
      familyName: this.$stringUtil.convertYen(this.name),
      familyTel: this.tel,
      familyRelation: this.$stringUtil.convertYen(this.relation),
      familyPosition: this.position ? this.$stringUtil.convertYen(this.position) : "",
      familyGender: this.gender,
      familyBirthDay: birthday,
      familyPermittedFunctions: this.permittedFunctions,
      senderType: this.senderType,
      updated: this.updated
    };

    return requestModel;
  }

  /**
   * ### 家族ユーザー一覧画面へ遷移する
   *
   * - 現在のURLに応じて戻り先を動的に判断する
   */
  public gotoBeforePage(): void {
    if (0 == this.$route.path.indexOf(PcUrl.ResidentFamilyEdit)) {
      this.$router.push(PcUrl.ResidentList);
    } else {
      this.$router.push(PcUrl.FamilyList);
    }

    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

// 親要素からネストしてスタイルを指定する
#family-edit {
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;

  .register-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
